/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getViewMode } from "selectors/viewModeSelectors";
import {
  shouldStartRendering,
  dialogOpened,
  getFileDisplayName,
  getFileName,
  getFileExtension,
  showToast,
  isSinglePageMode,
  getDocViewBgColor,
  getQuickToolsVisibility,
  getShouldShowPageLoader,
  isFilePasswordProtected,
  getShowStickyTools,
  getBottomPanelHeight,
  getStickyToolsLoaded
} from "selectors/documentSelectors";
import { DocumentView } from "components/document/DocumentView";
import { inject } from "mobx-react";
import {
  getIsHUDDocked,
  hudDisabled,
  getHudHandle,
  getHUDWidth,
  showProgressBar,
  showHUD
} from "selectors/hudSelectors";
import {
  getDocumentViewHandle,
  getViewPortWidthWithoutPagesViewScrollbar,
  getFallbackPrintLoaderStatus
} from "selectors/documentViewSelectors";
import { getMargin } from "selectors/zoom";
import {
  isZoomFeatureEnabled,
  getShouldUseModernViewer,
  getShouldUseMobileModernViewer,
  getSwitchPreviewState,
  getIsMultiDocModeEnabled
} from "selectors/appSelectors";
import { touchManager } from "../../touchManager";
import { contextMenuManager } from "contextMenuManager";
import { getPagesViewElementHandle, isInitialPageLoaded, isFirstPageLoaded } from "selectors/pagedViewState";
import { adjustViewPortHeight } from "utils/DocumentViewUtils";
import { isControlORCommandPressed } from "dc-preview-utils/src/Utils/EventHandlingUtils";
import { getAdressBarHeight } from "selectors/appUiStateSelectors";
import { A } from "dc-preview-utils/src/Constants/key-codes";
import { ADOBE_DOCUMENT_CLOUD } from "constants/constants";
import { hudHideManager } from "utils/ComponentHideManager";
import { isPersonalCommentingEnabled } from "utils/FeatureFlagUtils";
import { toastHideManager, bottomBoardHideManager } from "utils/ComponentHideManager";
import { isCurrentDeviceMobile, isCurrentDeviceTablet } from "dc-preview-utils/src/Utils/DeviceUtils";
import { getLiquidModeApis } from "publicApis/LiquidModeApi";
import { getDocumentStatus } from "selectors/accessibilitySelectors";
import { quickToolsVerbsPopulated } from "selectors/quickToolsSelectors";
import { getIsPanelAutoHideEnabled, getShouldShowBottomBanner } from "selectors/bottomBoardSelectors";
import { previewEvents } from "publicApis/Events/previewEvents";
import { SWITCH_PREVIEW_STATE } from "constants/enums";

const mapStoreToProps = (stores, ownProps) => {
  let documentName = "";
  if (getFileDisplayName(stores)) {
    documentName = `${getFileDisplayName(stores)} - ${ADOBE_DOCUMENT_CLOUD}`;
  }
  const { isDocumentInLiquidModeView } = getLiquidModeApis(stores);
  const isMultiDocModeEnabled = getIsMultiDocModeEnabled(stores);
  const switchPreviewState = getSwitchPreviewState(stores);
  const showQTOnSwitchPreview =
    isMultiDocModeEnabled &&
    (quickToolsVerbsPopulated(stores) /* First load */ ||
      switchPreviewState !== SWITCH_PREVIEW_STATE.NONE); /* Switching */
  const shouldStartLoadingQT =
    getFileExtension(stores).toLowerCase() === "pdf" ? isInitialPageLoaded(stores) : isFirstPageLoaded(stores);
  const shouldShowBottomBanner = getShouldShowBottomBanner(stores);
  return {
    showProgressBar: showProgressBar(stores),
    showFallbackLoader: getFallbackPrintLoaderStatus(stores),
    isSinglePageMode: isSinglePageMode(stores),
    enableZoomFeature: isZoomFeatureEnabled(stores),
    viewMode: getViewMode(stores),
    isReady: shouldStartRendering(stores),
    setDocumentView: ownProps.setDocumentView,
    documentName,
    docViewBgColor: getDocViewBgColor(stores),
    setHUDView: ownProps.setHUDView,
    isDialogOpen: dialogOpened(stores),
    pagedViewElementHandle: getPagesViewElementHandle(stores),
    isHUDDisabled: hudDisabled(stores),
    isHudDocked: getIsHUDDocked(stores),
    addressBarHeight: getAdressBarHeight(stores),
    supportsImmersiveMode: false, // Since we aren't supporting immersive mode (isCurrentDeviceMobile() || isCurrentDeviceTablet()) && !currLHPFeature(stores),
    fixedMargin: getMargin(stores),
    hudWidth: getHUDWidth(stores),
    showToast: showToast(stores),
    fileName: getFileName(stores),
    showHUD: showHUD(stores),
    isModernViewerEnabled: getShouldUseModernViewer(stores),
    shouldUseMobileModernViewer: getShouldUseMobileModernViewer(stores),
    showQuickTools:
      getShouldUseModernViewer(stores) &&
      getQuickToolsVisibility(stores) &&
      (showQTOnSwitchPreview || shouldStartLoadingQT),
    showStickyTools: getShowStickyTools(stores) && shouldStartLoadingQT,
    viewPortWidth: getViewPortWidthWithoutPagesViewScrollbar(stores),
    setDisplayToast: value => stores.documentStore.setDisplayToast(value),
    isPreviewable: ownProps.isPreviewable || isMultiDocModeEnabled,
    isFilePasswordProtected: isFilePasswordProtected(stores),
    adjustViewPortHeight: dH => adjustViewPortHeight(stores, dH),
    onTouchStart: event => {
      touchManager.handleTouchStart(event, stores);
      previewEvents.trigger("touchStart", {
        eventObj: event
      });
    },
    onTouchEnd: event => {
      touchManager.handleTouchEnd(event, stores);
      previewEvents.trigger("touchEnd", {
        eventObj: event
      });
    },
    onTouchCancel: event => {
      touchManager.handleTouchCancel(event, stores);
    },
    onTouchMove: event => {
      touchManager.handleTouchMove(event, stores);
    },
    setDocumentViewHandle: documentViewHandle => {
      stores.documentViewStore.setDocumentViewHandle(documentViewHandle);
    },
    onDoubleTap: event => {
      stores.zoom.doDoubleTapZoom(event);
    },
    onContextMenu: event => {
      if (isPersonalCommentingEnabled(stores)) {
        event.preventDefault();
        event.stopPropagation();
      } else {
        contextMenuManager.triggerContextMenu(event, stores);
      }
    },
    onKeyDown: event => {
      if (event.keyCode === A && isControlORCommandPressed(event)) {
        // removing it for now, as ctr+A and cmd+A functionality is not working on textBoxUI
        // event.preventDefault();
      }
    },
    setHUDLeft: left => {
      stores.hudStore.setLeft(left);
    },
    updateAddressBarHeight: () => {
      stores.appUiState.setAddressBarHeight(document.body.clientHeight - window.innerHeight);
    },
    handleMouseMove: evt => {
      const handle = getDocumentViewHandle(stores);
      if (!getIsHUDDocked(stores) && !hudDisabled(stores)) {
        hudHideManager.handleMouseOverHud(stores, getHudHandle(stores), evt);
        hudHideManager.handleMouseMove(stores, handle, evt);
      }
      if (!isCurrentDeviceMobile() && !isCurrentDeviceTablet()) {
        toastHideManager.handleMouseMove(stores, handle, evt);
      }
      if (!isCurrentDeviceMobile() && !isCurrentDeviceTablet() && getIsPanelAutoHideEnabled(stores)) {
        bottomBoardHideManager.handleMouseMove(stores, handle, evt);
      }
    },
    setHUDDockedWidth: dockedWidth => {
      stores.hudStore.setDockedWidth(dockedWidth);
    },
    setViewPortDimensions: (width, height) => {
      stores.documentViewStore.setViewPortDimensions(width, height);
    },
    onClickCapture: () => {
      stores.pagedViewState.unsetThumbnailIndexForFocus();
      stores.organizeStore.setSelectedSet(new Set());
    },
    setDocViewRects: rects => {
      stores.documentStore.setDocViewRects(rects);
    },
    getShouldShowPageLoader: getShouldShowPageLoader(stores),
    isLiquidModeViewEnabled: isDocumentInLiquidModeView(),
    documentStatus: getDocumentStatus(stores),
    bottomPanelHeight: getBottomPanelHeight(stores),
    setBottomPanelHeight: value => stores.documentStore.setBottomPanelHeight(value),
    stickyToolsLoaded: getStickyToolsLoaded(stores),
    switchPreviewState,
    shouldShowBottomBanner
  };
};

const DocumentContainer = inject(mapStoreToProps)(DocumentView);

export { DocumentContainer };
